/*
 * Notices (docs/ux-principles.md, "Notices"): how the app says that something happened, is wrong, or is coming up.
 * Each has a clear icon, a short title, an optional line, and at most one action.
 *
 *  - Callout: inside a card or a part, about this thing ("Live is locked with a password").
 *  - Banner: at the top of a page or a part, about the page's state ("Live isn't answering"). One per page at most.
 *  - Toasts (ui/Toast) say what just happened; field messages (ui/Field) sit under their field.
 *
 * Tones are the four status colors, fixed whatever the brand (lib/statusTone.ts): critical (red) for problems,
 * warning (amber) for what needs attention soon, good (green) for a good outcome, info (blue) for neutral notes and
 * scheduled things. Anything routine stays a plain line, not a box.
 */
import type { ComponentType, ReactNode } from "react";
import { cx } from "../lib/cx";
import { IconAlert, IconAlertCircle, IconCheckCircle, IconInfo, type IconProps } from "./icons";

export type NoticeTone = "critical" | "warning" | "good" | "info";

const TONE: Record<NoticeTone, { icon: ComponentType<IconProps>; box: string; mark: string; title: string; word: string }> = {
  critical: { icon: IconAlertCircle, box: "bg-bad-bg shadow-[inset_0_0_0_1px_var(--hx-bad-line)]", mark: "text-bad", title: "text-bad", word: "Problem" },
  warning: { icon: IconAlert, box: "bg-warn-bg shadow-[inset_0_0_0_1px_var(--hx-warn-line)]", mark: "text-warn", title: "text-fg", word: "Needs attention" },
  good: { icon: IconCheckCircle, box: "bg-good-bg shadow-[inset_0_0_0_1px_var(--hx-good-line)]", mark: "text-good", title: "text-fg", word: "Done" },
  info: { icon: IconInfo, box: "bg-info-bg shadow-[inset_0_0_0_1px_var(--hx-info-line)]", mark: "text-info", title: "text-fg", word: "Note" },
};

type NoticeProps = {
  tone?: NoticeTone;
  /** A few words: what it is. */
  title: ReactNode;
  /** One line more, when it helps: why, or what happens next. */
  children?: ReactNode;
  /** At most one: a button or a link. */
  action?: ReactNode;
  /** A different icon for the same tone (a clock for something scheduled). */
  icon?: ComponentType<IconProps>;
  /** "alert" for a problem that appears after something was done; otherwise nothing is announced on its own. */
  role?: "alert" | "status";
  className?: string;
};

/** Inside a card or a part: something about this thing. */
export function Callout({ tone = "info", title, children, action, icon, role, className }: NoticeProps) {
  const t = TONE[tone];
  const Icon = icon ?? t.icon;
  // A title alone sits on the action's center line; with text under it, both start at the top.
  const one = !children;
  return (
    <div role={role} className={cx("flex flex-wrap gap-x-3 gap-y-2 rounded-[10px] px-3.5 py-3", one ? "items-center" : "items-start", t.box, className)}>
      <div className={cx("flex min-w-[12rem] flex-1 gap-2.5", one ? "items-center" : "items-start")}>
        <Icon size={16} className={cx("shrink-0", !one && "mt-[2px]", t.mark)} />
        <div className="min-w-0">
          <p className={cx("text-[13.5px] leading-5 font-semibold", t.title)}>
            <span className="sr-only">{t.word}: </span>
            {title}
          </p>
          {children ? <div className="mt-0.5 text-[13px] leading-relaxed text-body">{children}</div> : null}
        </div>
      </div>
      {action ? <div className="ml-[26px] shrink-0 self-center sm:ml-0">{action}</div> : null}
    </div>
  );
}

/** At the top of a page or a part: the page's state. One per page at most. */
export function Banner({ tone = "info", title, children, action, icon, role, className }: NoticeProps) {
  const t = TONE[tone];
  const Icon = icon ?? t.icon;
  return (
    <div role={role} className={cx("flex flex-wrap items-center gap-x-4 gap-y-3 rounded-[var(--radius-card)] px-4 py-3.5 sm:px-5", t.box, className)}>
      <div className="flex min-w-[14rem] flex-1 items-start gap-3">
        <Icon size={18} className={cx("mt-px shrink-0", t.mark)} />
        <div className="min-w-0">
          <p className={cx("text-[14px] leading-5 font-semibold", t.title)}>
            <span className="sr-only">{t.word}: </span>
            {title}
          </p>
          {children ? <div className="mt-0.5 text-[13.5px] leading-relaxed text-body">{children}</div> : null}
        </div>
      </div>
      {action ? <div className="ml-[30px] shrink-0 sm:ml-0">{action}</div> : null}
    </div>
  );
}
